/**
 * Meta (Facebook) dual-channel lead tracking.
 *
 * Every lead submission fires TWO events that Meta deduplicates by a shared
 * `event_id`:
 *   1. Browser  – Meta Pixel `Lead` event (this file).
 *   2. Server   – Conversions API `Lead` event (send-enquiry-email -> meta-capi-send).
 *
 * The returned object is spread into the lead payload so the backend can send
 * the matching server-side event with the same identifiers.
 */

import { getTrackingData, getMetaIdentifiers } from "@/hooks/useTrackingData";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export interface LeadEventOptions {
  /** Estimated AUD value of the enquiry, when known. */
  value?: number;
  /** Product or form name shown to Meta. */
  contentName?: string;
  /** Product category shown to Meta. */
  contentCategory?: string;
  /** Pixel event name. Defaults to "Lead". */
  eventName?: string;
}

function newEventId(): string {
  const rand = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
    : Math.random().toString(36).slice(2, 14);
  return `lead_${Date.now()}_${rand}`;
}

/**
 * Fires the browser Pixel `Lead` event and returns the attribution fields to
 * attach to the lead payload (including the shared event id for dedupe).
 */
export function trackLeadSubmission(options: LeadEventOptions = {}) {
  const tracking = getTrackingData();
  const { fbp, fbc, fbclid } = getMetaIdentifiers();
  const meta_event_id = newEventId();
  const eventName = options.eventName || "Lead";

  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      const customData: Record<string, unknown> = { currency: "AUD" };
      if (options.value !== undefined && Number.isFinite(options.value)) {
        customData.value = Number(options.value.toFixed(2));
      }
      if (options.contentName) customData.content_name = options.contentName;
      if (options.contentCategory) customData.content_category = options.contentCategory;
      window.fbq("track", eventName, customData, { eventID: meta_event_id });
    }
  } catch {
    /* never block a submission because of tracking */
  }

  return {
    ...tracking,
    fbp,
    fbc,
    fbclid,
    meta_event_id,
    meta_event_name: eventName,
    client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    meta_value: options.value,
    meta_content_name: options.contentName,
    meta_content_category: options.contentCategory,
  };
}
